/**
 * Path: packages/frontend/src/core/composables/useEntityOperations.ts
 * Task: Controller handling complex domain logic (Creation, Saving, Splitting, Duplication, Deletion, Analytics) across stores.
 * Update: Extracted global cycle detection and resolution logic to be reusable in headers and dashboards.
 */
import { computed } from 'vue';
import { useAuthStore } from '@/store/auth';
import { useProjectStore } from '@/store/project';
import { useDatastoreStore } from '@/store/datastore';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { EntityService } from '@/core/services/entityService';
import type { Entity } from '@/core/types/editor';

export interface CycleBreak {
  source: any;
  target: any;
}

export interface CycleGroup {
  id: string;
  nodes: any[];
  suggestedBreak: CycleBreak;
}

export function useEntityOperations() {
  const authStore = useAuthStore();
  const projectStore = useProjectStore();
  const datastore = useDatastoreStore();
  const workspace = useWorkspaceStore();
  const uiStore = useUiStore();

  const generateNextId = () => {
    const projectId = projectStore.loadedProject?.value || 'ENT';
    return EntityService.generateNextEntityId(`${projectId}-`, datastore.allEntities, [
      workspace.selectedEntity?.id,
      workspace.secondarySelectedEntity?.id,
      workspace.draftEntity?.id
    ]);
  };

  const createNewEntity = (entityType: string, initialData: Record<string, any> = {}) => {
    const entitiesDef = authStore.appData?.entities || [];
    const newId = generateNextId();
    const newEntity = EntityService.createBlankEntity(entityType, newId, entitiesDef, datastore.data);

    // Apply any predefined initial data (like predecessors or successors)
    Object.keys(initialData).forEach(key => {
      newEntity[key] = initialData[key];
    });

    workspace.draftEntity = JSON.parse(JSON.stringify(newEntity));
    workspace.isNewEntityDialogOpen = true;
  };

  const saveEntity = async (isSecondary = false, isDraft = false) => {
    const activeEntity = isDraft ? workspace.draftEntity : (isSecondary ? workspace.secondarySelectedEntity : workspace.selectedEntity);
    const originalEntity = isDraft ? null : (isSecondary ? workspace.secondaryOriginalEntity : workspace.originalEntity);

    if (!activeEntity || !datastore.data) return;

    activeEntity.lastUpdated = new Date().toISOString();
    activeEntity.updatedBy = authStore.user?.email || 'Unknown';

    // Generate history entry for BOTH creation (draft) and updates
    const historyEntry = EntityService.generateHistoryEntry(originalEntity, activeEntity, authStore.user?.email || 'Unknown');
    if (historyEntry) {
      if (!activeEntity.history) activeEntity.history = [];
      activeEntity.history.push(historyEntry);
    }

    uiStore.loading.save = true;
    try {
      const relationsDef = authStore.appData?.relations || [];
      EntityService.saveEntityToDatastore(activeEntity, datastore.data, relationsDef);
      datastore.setDirty(true);

      const projectId = projectStore.loadedProject?.value;
      if (projectId) {
        await datastore.saveProjectDatastore(projectId);
        await datastore.fetchProjectDatastore(projectId);
        await projectStore.fetchProjectStats(projectId);

        if (isDraft) {
          workspace.isNewEntityDialogOpen = false;
          authStore.showNotification('Entity created successfully!', 'success');
          if (isSecondary) workspace.setSecondarySelectedEntity(activeEntity.id);
          else workspace.setSelectedEntity(activeEntity.id);
        } else {
          authStore.showNotification('Saved.', 'success');
        }

        setTimeout(() => window.dispatchEvent(new CustomEvent('maps-refresh')), 50);
      }
    } catch (error: any) {
      authStore.showNotification(`Error saving entity: ${error.message}`, 'error');
    } finally {
      uiStore.loading.save = false;
    }
  };

  const duplicateEntity = async (isSecondary = false) => {
    const activeEntity = isSecondary ? workspace.secondarySelectedEntity : workspace.selectedEntity;
    if (!activeEntity) return;

    const newId = generateNextId();
    const duplicated = EntityService.duplicateEntity(activeEntity, newId);

    // Provide the duplicated entity as a draft so the user can edit it before saving
    workspace.draftEntity = duplicated;
    workspace.isNewEntityDialogOpen = true;
  };

  const splitEntity = async (isSecondary = false) => {
    const activeEntity = isSecondary ? workspace.secondarySelectedEntity : workspace.selectedEntity;
    if (!activeEntity || !datastore.data) return;

    uiStore.loading.save = true;
    try {
      const relationsDef = authStore.appData?.relations || [];

      // 1. Create the "End" part
      const newId = generateNextId();
      const endEntity = EntityService.duplicateEntity(activeEntity, newId);
      endEntity.title = `${activeEntity.title} (End)`;

      // 2. Modify the "Start" part (current entity)
      activeEntity.title = `${activeEntity.title} (Start)`;

      // 3. Link them together (Start -> End)
      if (!activeEntity.successorIds) activeEntity.successorIds = [];
      if (!activeEntity.successorIds.includes(endEntity.id)) activeEntity.successorIds.push(endEntity.id);

      if (!endEntity.predecessorIds) endEntity.predecessorIds = [];
      if (!endEntity.predecessorIds.includes(activeEntity.id)) endEntity.predecessorIds.push(activeEntity.id);

      // 4. Save both to local datastore
      EntityService.saveEntityToDatastore(endEntity, datastore.data, relationsDef);
      EntityService.saveEntityToDatastore(activeEntity, datastore.data, relationsDef);

      datastore.setDirty(true);

      const projectId = projectStore.loadedProject?.value;
      if (projectId) {
        await datastore.saveProjectDatastore(projectId);
        await datastore.fetchProjectDatastore(projectId);
        await projectStore.fetchProjectStats(projectId);

        authStore.showNotification('Entity successfully split into Start and End.', 'success');
        setTimeout(() => window.dispatchEvent(new CustomEvent('maps-refresh')), 50);
      }
    } catch (error: any) {
      authStore.showNotification(`Error splitting entity: ${error.message}`, 'error');
    } finally {
      uiStore.loading.save = false;
    }
  };

  const deleteEntity = async (isSecondary = false) => {
    const activeEntity = isSecondary ? workspace.secondarySelectedEntity : workspace.selectedEntity;
    if (!activeEntity || !datastore.data) return;

    uiStore.loading.save = true;
    try {
      // Deletes the entity and scrubs all references from other objects
      EntityService.deleteEntityFromDatastore(activeEntity.id, activeEntity.entityType, datastore.data);
      datastore.setDirty(true);

      const projectId = projectStore.loadedProject?.value;
      if (projectId) {
        await datastore.saveProjectDatastore(projectId);
        await datastore.fetchProjectDatastore(projectId);
        await projectStore.fetchProjectStats(projectId);

        authStore.showNotification('Entity permanently deleted.', 'success');

        // Close the panel that held the deleted entity
        if (isSecondary) {
          workspace.closeSecondarySelectedEntity();
        } else {
          workspace.setSelectedEntity(null);
        }

        setTimeout(() => window.dispatchEvent(new CustomEvent('maps-refresh')), 50);
      }
    } catch (error: any) {
      authStore.showNotification(`Error deleting entity: ${error.message}`, 'error');
    } finally {
      uiStore.loading.save = false;
    }
  };

  /**
   * Actionable Analytics: Calculates the average in-degree (incoming complexity) of the current workflow graph.
   */
  const averageIncomingComplexity = computed(() => {
    const entities = datastore.allEntities || [];

    // To get a meaningful average, we strictly isolate nodes that are actually part
    // of the workflow (having at least one structural connection).
    const workflowNodes = entities.filter(e =>
      (Array.isArray(e.predecessorIds) && e.predecessorIds.length > 0) ||
      (Array.isArray(e.successorIds) && e.successorIds.length > 0)
    );

    if (workflowNodes.length === 0) return 0;

    let totalIncomingEdges = 0;
    workflowNodes.forEach(e => {
      totalIncomingEdges += Array.isArray(e.predecessorIds) ? e.predecessorIds.length : (e.predecessorIds ? 1 : 0);
    });

    return totalIncomingEdges / workflowNodes.length;
  });

  /**
   * Helper function to detect if an entity is a structural bottleneck and a candidate for splitting.
   * Uses a dynamic network analysis heuristic (In-Degree > Average).
   */
  const getBottleneckSeverity = (entity: Entity | null | undefined): number => {
    if (!entity || !entity.predecessorIds) return 0;
    const count = Array.isArray(entity.predecessorIds) ? entity.predecessorIds.length : (entity.predecessorIds ? 1 : 0);

    // Heuristic Rules:
    // 1. A split ONLY makes physical sense if there are at least 2 incoming edges (min threshold 1.5).
    // 2. We compare against the graph's average complexity plus a tiny buffer (+0.5)
    //    to effectively prevent "alert fatigue" in naturally highly interconnected maps.
    const dynamicThreshold = Math.max(1.5, averageIncomingComplexity.value + 0.5);

    return count > dynamicThreshold ? count : 0;
  };

  /**
   * Identifies circular dependencies within the datastore and returns Actionable Analytics
   * heuristics determining which specific edge should be cut to resolve them.
   */
  const detectCycles = (): CycleGroup[] => {
    const entities = datastore.allEntities || [];

    const sequenceRelations = authStore.appData?.relations?.filter((r: any) => r.semanticType === 'sequence') || [];
    const workflowTypes = new Set<string>();

    sequenceRelations.forEach((r: any) => {
      if (r.sourceTypes) r.sourceTypes.forEach((t: string) => workflowTypes.add(t));
      if (r.targetTypes) r.targetTypes.forEach((t: string) => workflowTypes.add(t));
    });

    const typesArray = workflowTypes.size > 0 ? Array.from(workflowTypes) : ['artifacts'];

    const entityMap = new Map(entities.map(e => [e.id, e]));

    const getSuccessors = (node: any): string[] => {
      const targets = new Set<string>();
      sequenceRelations.forEach((rel: any) => {
        if (rel.sourceTypes?.includes(node.entityType)) {
          const outProp = rel.id === 'predecessors' ? 'successorIds' : (rel.isMultiple ? `${rel.id}Ids` : `${rel.id}Id`);
          const vals = node[outProp];
          if (Array.isArray(vals)) vals.forEach((v: string) => targets.add(v));
          else if (vals) targets.add(vals);
        }
      });
      return Array.from(targets);
    };

    const cycles: Array<string[]> = [];
    const visited = new Set<string>();
    const recStack = new Set<string>();

    const findCycle = (nodeId: string, path: string[]) => {
      if (recStack.has(nodeId)) {
        const cycleIds = path.slice(path.indexOf(nodeId));
        if (cycleIds.length > 0) cycles.push(cycleIds);
        return;
      }

      if (visited.has(nodeId)) return;

      visited.add(nodeId);
      recStack.add(nodeId);
      path.push(nodeId);

      const node = entityMap.get(nodeId);
      if (node) {
        getSuccessors(node).forEach(succId => findCycle(succId, [...path]));
      }

      recStack.delete(nodeId);
    };

    entities.forEach(e => {
      if (typesArray.includes(e.entityType) && !visited.has(e.id)) {
        findCycle(e.id, []);
      }
    });

    const uniqueCycles = new Map<string, CycleGroup>();

    cycles.forEach(c => {
      const sortKey = [...c].sort().join(',');
      if (!uniqueCycles.has(sortKey)) {
        const resolvedNodes = c.map(id => entityMap.get(id)).filter(e => !!e);

        if (resolvedNodes.length > 0) {
          // Default: Assume the edge closing the loop is the backward loop (last -> first)
          let breakSource = resolvedNodes[resolvedNodes.length - 1];
          let breakTarget = resolvedNodes[0];

          // Advanced Heuristic: Look for an edge where source Rank > target Rank
          // (Typically implies someone linked a later step back to an earlier step incorrectly)
          for(let i = 0; i < resolvedNodes.length; i++) {
            const src = resolvedNodes[i];
            const tgt = resolvedNodes[(i + 1) % resolvedNodes.length];
            if (src.rank !== undefined && tgt.rank !== undefined && src.rank > tgt.rank) {
              breakSource = src;
              breakTarget = tgt;
              break;
            }
          }

          uniqueCycles.set(sortKey, {
            id: sortKey,
            nodes: resolvedNodes,
            suggestedBreak: { source: breakSource, target: breakTarget }
          });
        }
      }
    });

    return Array.from(uniqueCycles.values());
  };

  /**
   * Cleanly severs an edge between a source and a target to resolve feedback cycles,
   * triggering a complete backend sync and analytics refresh.
   */
  const resolveCycleEdge = async (sourceNode: any, targetNode: any): Promise<void> => {
    const sequenceRelations = authStore.appData?.relations?.filter((r: any) => r.semanticType === 'sequence') || [];

    sequenceRelations.forEach((rel: any) => {
      if (rel.sourceTypes?.includes(sourceNode.entityType)) {
        const outProp = rel.id === 'predecessors' ? 'successorIds' : (rel.isMultiple ? `${rel.id}Ids` : `${rel.id}Id`);
        const vals = sourceNode[outProp];

        if (Array.isArray(vals)) {
          sourceNode[outProp] = vals.filter((v: string) => v !== targetNode.id);
        } else if (vals === targetNode.id) {
          sourceNode[outProp] = null;
        }

        const inProp = rel.id === 'predecessors' ? 'predecessorIds' : `inverse_${rel.id}Ids`;
        if (Array.isArray(targetNode[inProp])) {
          targetNode[inProp] = targetNode[inProp].filter((v: string) => v !== sourceNode.id);
        }
      }
    });

    datastore.setDirty(true);

    const projectId = projectStore.loadedProject?.value;
    if (projectId) {
      await datastore.saveProjectDatastore(projectId);
      await projectStore.fetchProjectStats(projectId);
      authStore.showNotification(`Feedback loop cleanly severed.`, 'success');
      window.dispatchEvent(new CustomEvent('maps-refresh'));
    }
  };

  return {
    generateNextId,
    createNewEntity,
    saveEntity,
    duplicateEntity,
    splitEntity,
    deleteEntity,
    getBottleneckSeverity,
    detectCycles,
    resolveCycleEdge
  };
}
